import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class VatCode extends ModelObjectWithDirtyCheck<VatCode> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * The Code used to communicate this Vat Code to the accounts package for Sales of items with this VAT code. For Sage this is typically 'T0', 'T1', etc. For Xero it is typically 'Standard (20%)', 'Exempt Income', etc.
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public accountsCodeForSales: string = "";
    /**
    * The Code used to communicate this Vat Code to the accounts package for Purchases of items with this Vat Code. Only required if different to the code for sales. (Code for sales will be used if this is empty).  For Sage this is typically empty as Sage does not distinguish Vat types between sales and purchases.  For Xero it is typically '20% (VAT on Expenses)', 'Exempt Expeses', etc.
    * Not Nullable
    * Max Length : 1000
    */
    public accountsCodeForPurchases: string = "";
    /**
    * Default Vat Code for new outlets.
    */
    public isDefault: boolean = false;
    /**
    * Exempt Code
    */
    public isExempt: boolean = false;
    /**
    * Export Code
    */
    public isExport: boolean = false;
    /**
    * Name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";
    /**
    * VAT Percentage
    * Not Negative
    */
    public vatPercentage: number = 0;
    public isHidden: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.accountsCodeForSales = jsonSerializedObject.accountsCodeForSales;
          this.accountsCodeForPurchases = jsonSerializedObject.accountsCodeForPurchases;
          this.isDefault = jsonSerializedObject.isDefault;
          this.isExempt = jsonSerializedObject.isExempt;
          this.isExport = jsonSerializedObject.isExport;
          this.name = jsonSerializedObject.name;
          this.vatPercentage = jsonSerializedObject.vatPercentage;
          this.isHidden = jsonSerializedObject.isHidden;
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.accountsCodeForSales = this.accountsCodeForSales;
        toRet.accountsCodeForPurchases = this.accountsCodeForPurchases;
        toRet.isDefault = this.isDefault;
        toRet.isExempt = this.isExempt;
        toRet.isExport = this.isExport;
        toRet.name = this.name;
        toRet.vatPercentage = this.vatPercentage;
        toRet.isHidden = this.isHidden;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "VatCode.Id cannot be empty"},
            {field: "accountsCodeForSales", rule: (v: any) => v == null || v.length !== 0 || "AccountsCodeForSales cannot be empty"},
            {field: "accountsCodeForSales", rule: (v: any) => v == null || v.length <= 1000 || "AccountsCodeForSales has too many characters (1000)"},
            {field: "accountsCodeForPurchases", rule: (v: any) => v == null || v.length <= 1000 || "AccountsCodeForPurchases has too many characters (1000)"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
    ];
}


export class VatCodeService extends ServiceBase {

  public async getAllVatCodes(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(VatCode)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.vatCodes.map((v: any) => new VatCode(v));;
    return this.makeJsonRequest("vatCode/v1/getAllVatCodes", req, false, extractor, opts);
  };

  public async getVatCode(vatCodeId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<VatCode> {
    const req = {
    tenantId:api.tenantId.toString(),
    vatCodeId:vatCodeId.toString(),
    };
    const extractor = (response:any) => new VatCode(response.vatCode);;
    return this.makeJsonRequest("vatCode/v1/getVatCode", req, false, extractor, opts);
  };

  public async getVatCodes(vatCodeIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(VatCode)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    vatCodeIds:vatCodeIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.vatCodes.map((v: any) => new VatCode(v));;
    return this.makeJsonRequest("vatCode/v1/getVatCodes", req, false, extractor, opts);
  };

  public async saveVatCodes(vatCodes: readonly VatCode[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    vatCodes:vatCodes.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("vatCode/v1/saveVatCodes", req, true, extractor, opts);
  };

  public async setVatCodeIsHidden(vatCodeId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    vatCodeId:vatCodeId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("vatCode/v1/setVatCodeIsHidden", req, true, extractor, opts);
  };
}

export const vatCodeService = new VatCodeService();